import el from "@cypherpotato/el";
import page from "page";
import { Paragraph } from "./components/text";
import { getSession, isAuthed } from "./session";
import { displayAlert } from "./ui/alert";
import { showSuccessToast } from "./ui/toast";

const fetch = window.fetch.bind(window); // prevent monkey patching
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000;
function getRetryDelay(attempt) {
    const backoffDelay = INITIAL_RETRY_DELAY * Math.pow(2, attempt);
    const jitter = Math.floor(Math.random() * 250);
    return backoffDelay + jitter;
}

export async function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

async function apiFetch(url, options) {
    var session = getSession();
    var headers = options?.headers || {};

    if (session.sessionKey) {
        headers["Authorization"] = `Bearer ${session.sessionKey}`;
    }

    const maxRetries = options?.maxRetries ?? MAX_RETRIES;
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            const response = await fetch(window.env.apiEndpoint + url, {
                method: options?.method || "GET",
                headers: headers,
                body: options?.body,
                signal: options?.signal,
                credentials: "include"
            });

            if (response.status === 401) {
                localStorage.clear();
                window.redirectAfterLogin = window.location.pathname + window.location.search;
                page("/");
            }

            return response;
        } catch (error) {
            lastError = error;

            if (error?.name === "AbortError" || attempt === maxRetries) {
                throw error;
            }

            const delay = getRetryDelay(attempt);
            console.warn(`Connection error (attempt ${attempt + 1}/${maxRetries + 1}): ${error.message}. Retrying in ${delay}ms...`);
            await sleep(delay);
        }
    }

    throw lastError;
}

async function parseResultJson(response) {
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
        return null;
    }
    const responseJson = await response.json();
    if (responseJson.error) {
        displayErrors(responseJson);

    } else if (responseJson.message) {
        showSuccessToast(responseJson.message);
    }

    return responseJson;
}

async function unbodyJson(method, url, options) {
    const response = await apiFetch(url, {
        method,
        ...options
    });
    return await parseResultJson(response);
}

async function bodyJson(method, url, data, options) {
    const response = await apiFetch(url, {
        method,
        body: JSON.stringify(data),
        ...options
    });
    return await parseResultJson(response);
}

function displayErrors(responseJson) {
    if (responseJson.details?.validationErrors) {
        displayAlert({
            title: "Warning",
            message: el.fragment(
                Paragraph(responseJson.error),
                el("ul",
                    responseJson.details.validationErrors.map(x =>
                        el("li", el("div", x.memberName + ": "), el("div.text-bold", x.error))
                    )
                )
            ),
            type: "alert-warning",
            buttons: [
                {
                    label: "OK",
                    onClick(event) {
                        event.target.closest("dialog").dismiss();
                    }
                }
            ]
        })

    } else {
        displayAlert({
            title: "Warning",
            message: responseJson.error,
            type: "alert-warning",
            buttons: [
                {
                    label: "OK",
                    onClick(event) {
                        event.target.closest("dialog").dismiss();
                    }
                }
            ]
        })
    }
}

function createEventSource(url) {
    const path = new URL(window.env.apiEndpoint + url);
    if (isAuthed()) {
        path.searchParams.append("api-key", getSession().sessionKey);
    }

    const eventSource = new EventSource(path.toString());
    return eventSource;
}

async function unbodyReader(method, url, options) {
    const response = await apiFetch(url, {
        method,
        body: JSON.stringify(options.data),
        signal: options?.signal
    });

    if (!response.ok) {
        const responseJson = await response.json();
        displayErrors(responseJson);
    }

    return createReader(response, options);
}

async function createReader(response, opt) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    while (true) {

        if (opt.signal.aborted) {
            reader.cancel();
            break;
        }

        const { value, done } = await reader.read();
        if (done) {
            buffer += decoder.decode();
            if (buffer.length > 0) {
                opt.onreadline(buffer);
            }
            break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
            if (line.trim()?.length > 0) {
                opt.onreadline(line);
            }
        }
    }
}

export const api = {
    getJson: unbodyJson.bind(null, "GET"),
    deleteJson: unbodyJson.bind(null, "DELETE"),
    postJson: bodyJson.bind(null, "POST"),
    putJson: bodyJson.bind(null, "PUT"),
    patchJson: bodyJson.bind(null, "PATCH"),
    postReader: unbodyReader.bind(null, "POST"),
    createEventSource,
    fetch: apiFetch,
    parseBody: parseResultJson
};
